import { useContext, useEffect } from "react";
import { CampaignContext } from "../../Context";
import {
  Box,
  Grid,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { SubmitCampaign } from "types/Campaign";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { initialSubmitCampaignFields } from "../../Constants";
import { cloneDeep } from "lodash";
import { convertESTtoUserLocalDateAndTime, isTruthy } from "helpers/methods";
import moment from "moment";

interface CustomProps {
  setChildCampaigns: Function;
  childCampaigns: SubmitCampaign[];
  setActiveStep: Function;
  isTimePast: boolean;
}

const ChildCampaigns = (props: CustomProps) => {
  const { managingStep, setSubmitCampaign } = useContext(CampaignContext);

  useEffect(() => {
    if (props.isTimePast) {
      props.childCampaigns.map((child, index: number) => {
        stepCompletion(index);
      });
    }
  }, [props.isTimePast]);

  const checkForPastTimeError = (index: number) => {
    const now = moment().seconds(0).milliseconds(0);
    const localTime = moment(
      convertESTtoUserLocalDateAndTime(
        props.childCampaigns[index].campaign.scheduleTime,
        "MM/DD/YYYY HH:mm:ss",
        "MM/DD/YYYY HH:mm:ss"
      ),
      "MM/DD/YYYY HH:mm:ss"
    )
      .seconds(0)
      .milliseconds(0);
    return now.isSameOrAfter(localTime);
  };

  const stepCompletion = (index: number) => {
    const campaign = props.childCampaigns[index].campaign;
    const isPast = checkForPastTimeError(index);
    return (
      isTruthy(campaign.subject) &&
      isTruthy(campaign.content) &&
      isTruthy(campaign.scheduleTime) &&
      !isPast
    );
  };

  const addChildCampaign = () => {
    const childCampaign = initialSubmitCampaignFields(
      "Regular"
    ) as SubmitCampaign;
    childCampaign.campaignSegments = props.childCampaigns[0].campaignSegments;
    childCampaign.campaign.id = new Date().getMilliseconds();
    childCampaign.campaign.name = `Untitled Campaign ${
      props.childCampaigns.length + 1
    }`;
    childCampaign.campaign.fromId = props.childCampaigns[0].campaign.fromId;
    childCampaign.campaign.displayName =
      props.childCampaigns[0].campaign.displayName;
    childCampaign.campaign.fromIdEmail =
      props.childCampaigns[0].campaign.fromIdEmail;
      childCampaign.campaign.senderEmail =
      props.childCampaigns[0].campaign.senderEmail;
    childCampaign.campaign.replyToEmail =
      props.childCampaigns[0].campaign.replyToEmail;
    props.setChildCampaigns([...props.childCampaigns, childCampaign]);
  };

  const getPreview = () => {
    const campaignCount = props.childCampaigns.length;
    return (
      <Grid container spacing={2}>
        {props.childCampaigns.map((child, index: number) => {
          return (
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                {child.campaign.content ? (
                  <iframe
                    srcDoc={child.campaign.content}
                    height="250px"
                    style={{
                      border: "1px solid rgba(71, 85, 105, 0.25)",
                      borderRadius: "12px",
                    }}
                  />
                ) : (
                  <Stack
                    height="250px"
                    width="100%"
                    sx={{ backgroundColor: "#DCE2EB", borderRadius: "12px" }}
                  ></Stack>
                )}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={0.5}>
                    <SvgIcon>
                      <CheckCircleIcon
                        htmlColor={
                          stepCompletion(index) ? "#7551D0" : "#C1C1C1"
                        }
                      />
                    </SvgIcon>
                    <Typography>{child.campaign.name}</Typography>
                  </Stack>
                  <Stack direction="row">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSubmitCampaign(child);
                        props.setActiveStep(1);
                      }}
                    >
                      <SvgIcon fontSize="small">
                        <ModeEditIcon fontSize="small" />
                      </SvgIcon>
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(event: any) => {
                        const clonedChildCampaigns = cloneDeep(
                          props.childCampaigns
                        );
                        clonedChildCampaigns.splice(index, 1);
                        props.setChildCampaigns(clonedChildCampaigns);
                      }}
                      disabled={campaignCount < 3}
                    >
                      <SvgIcon fontSize="small">
                        <DeleteIcon fontSize="small" />
                      </SvgIcon>
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </Grid>
          );
        })}
        {props.childCampaigns.length < 4 && (
          <Grid item xs={12} md={4}>
            <Stack
              height="250px"
              width="100%"
              alignItems="center"
              justifyContent="center"
            >
              <IconButton size="large" onClick={addChildCampaign}>
                <SvgIcon fontSize="large">
                  <AddCircleOutlineIcon fontSize="large" />
                </SvgIcon>
              </IconButton>
              <Typography variant="body1">Add more campaign</Typography>
              <Typography variant="body1">{`(${
                props.childCampaigns.length + 1
              }/4)`}</Typography>
            </Stack>
          </Grid>
        )}
      </Grid>
    );
  };

  const getView = () => {
    const onAnotherStep =
      managingStep !== "" && managingStep !== "child-campaigns";
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ opacity: onAnotherStep ? 0.5 : 1 }}
        spacing={4}
      >
        <Stack width="100%">
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Stack spacing={0.5}>
              <Typography variant="h4">Campaigns</Typography>
              <Typography variant="body1">
                Click on the edit icon to manage the campaign
              </Typography>
            </Stack>
          </Stack>
          {getPreview()}
        </Stack>
      </Stack>
    );
  };

  const getPage = () => {
    return getView();
  };

  return <Box px={3}>{getPage()}</Box>;
};

export default ChildCampaigns;
